import React from "react";
import PageShell from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { ACKNOWLEDGEMENTS } from "@/data/content";

const SECTIONS = [
  {
    key: "mentors",
    title: "Mentors",
    lead: "People who shaped standards, judgment, work ethic, or worldview.",
    editorial:
      "A mentor is not always titled. Sometimes the standard-setter is a coach, a parent, a customer, a boss who refused to lower a bar, or a competitor who forced a rewrite. The names that appear here will be the ones whose fingerprints show up in how the work is done, not only in what was done.",
    principle: "Mentorship is measured in what changed, not what was said.",
  },
  {
    key: "collaborators",
    title: "Collaborators",
    lead: "People building alongside.",
    editorial:
      "Collaboration is more than credit. It is the small daily decisions to think together, defend the work, and keep the standard from drifting. The people acknowledged here are ones the work would not exist without.",
    principle: "The best collaborators sharpen a project by refusing to accept a soft draft.",
  },
  {
    key: "influences",
    title: "Influences",
    lead: "Books, conversations, and rooms that bent the line.",
    editorial:
      "Influence is often invisible. It shows up in the questions that get asked instinctively, the analogies that keep returning, the phrases that survived because they were true. This section will name the sources deliberately, so credit is not accidental.",
    principle: "An idea has authors. Even when it feels original, someone taught the eye to see it.",
  },
  {
    key: "partners",
    title: "Partners",
    lead: "Organizations and individuals connected to meaningful work.",
    editorial:
      "Partners are named only when the relationship is real, sustained, and mutual. This is not a logo wall or a proximity board. Presence here indicates work that was actually done together.",
    principle: "A real partner absorbs risk on both sides of a decision.",
  },
];

export default function Acknowledgements() {
  return (
    <PageShell
      testId="page-acknowledgements"
      title="Acknowledgements | Quentin Young"
      description="Acknowledging mentors, collaborators, influences, and partners who have helped shape Quentin Young's thinking, work, and direction."
      ogImage="/og-acknowledgements.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Acknowledgements", url: "/acknowledgements" }]}
    >
      <section className="container-edge pt-16 md:pt-24 pb-16">
        <p className="eyebrow-gold mb-5">Acknowledgements</p>
        <h1 className="display-serif text-4xl md:text-6xl lg:text-7xl text-paper-primary text-balance leading-[1.02]">
          No one becomes who they are alone.
        </h1>
        <p className="mt-8 max-w-2xl font-serif text-lg md:text-xl text-paper-secondary leading-relaxed text-pretty">
          {ACKNOWLEDGEMENTS.intro}
        </p>
      </section>

      {SECTIONS.map((sec) => {
        const entries = ACKNOWLEDGEMENTS[sec.key] || [];
        return (
          <section key={sec.key} className="bg-ink-base border-t border-ink-line py-20 md:py-24">
            <div className="container-edge">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                  <p className="eyebrow mb-4">{sec.title}</p>
                  <p className="font-serif text-2xl text-paper-primary">{sec.lead}</p>
                </div>
                {entries.length === 0 ? (
                  <div className="lg:col-span-8 border border-ink-line bg-ink-surface/40 p-10 md:p-12" data-testid={`ack-empty-${sec.key}`}>
                    <p className="eyebrow-gold mb-4">Editorial context</p>
                    <p className="text-paper-secondary text-base leading-relaxed text-pretty">
                      {sec.editorial}
                    </p>
                    <p className="mt-6 font-serif italic text-lg text-paper-primary text-balance">
                      {sec.principle}
                    </p>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">
                      Names appear only with consent. This section is written before it is populated, on purpose.
                    </p>
                  </div>
                ) : (
                  <ul className="lg:col-span-8 space-y-px bg-ink-line">
                    {entries.map((entry, i) => (
                      <li key={`${sec.key}-${i}`} className="bg-ink-base p-8 md:p-10" data-testid={`ack-${sec.key}-${i}`}>
                        <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-3">
                          <h3 className="font-serif text-2xl text-paper-primary">{entry.name}</h3>
                          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">{entry.relationship}</p>
                        </div>
                        <p className="mt-4 text-paper-secondary leading-relaxed">{entry.note}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>
        );
      })}

      <section className="bg-ink-surface border-t border-ink-line py-16">
        <div className="container-edge">
          <p className="font-serif italic text-paper-tertiary text-lg max-w-2xl">
            This page grows slowly and on purpose. Endorsement is not implied. Permission is required.
          </p>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Every name here left fingerprints on the work."
        rooms={[
          { label: "Explore the work they influenced", to: "/building", hint: "proof" },
          { label: "Read the related thinking", to: "/thinking", hint: "thinking" },
          { label: "Return to the timeline", to: "/about", hint: "origin" },
        ]}
        testId="ack-next"
      />
    </PageShell>
  );
}
